import React, { useState, useEffect, useMemo } from "react";
import { Modal, Input, List, Spin, Badge, Empty, Button } from "antd";
import PhoneInput from "react-phone-input-2";
import toast from "react-hot-toast";
import axios from "axios";
import debounce from "lodash/debounce";
import { API_URL } from "../../config/config";
import { getHeaderOptions } from "../../services/getHeaderOptions";
import VirtualList from "rc-virtual-list";
import { Logger } from '../../utils/logger';
import { getTemplates } from "../../services/templates";
import { useAppDispatch } from "../../hooks/hooks";
import { sendTemplate } from "../../store/slices/messagesSlice";
import { fetchConversations } from "../../store/slices/conversationsSlice";
import { ArrowLeft, Check, MessageSquare } from "lucide-react";

interface AddChatProps {
  visible: boolean;
  onClose: () => void;
  // Legacy prop - kept for backward compatibility but no longer used
  // Template sending is now handled internally via Redux
  onStartChat?: (data: { phone: string; name?: string }) => Promise<void>;
}

interface SelectedLead {
  phone: string;
  name?: string;
}

interface Template {
  _id: string;
  name: string;
  status: string;
  category?: string;
  language?: string;
  components?: Array<{
    type: string;
    text?: string;
    format?: string;
  }>;
}

const CONTAINER_HEIGHT = 400;
const ITEM_HEIGHT = 64;

const AddChat: React.FC<AddChatProps> = ({ visible, onClose, onStartChat }) => {
  const dispatch = useAppDispatch();

  // Step management: 'search' | 'template'
  const [step, setStep] = useState<'search' | 'template'>('search');

  // Lead search state
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");

  // Selected lead state
  const [selectedLead, setSelectedLead] = useState<SelectedLead | null>(null);

  // Template state
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [sendingTemplate, setSendingTemplate] = useState(false);
  const [templateSearchTerm, setTemplateSearchTerm] = useState("");

  // Reset states when modal opens OR closes
  useEffect(() => {
    if (!visible) {
      // Reset everything when modal closes
      setStep('search');
      setSearchResults([]);
      setSearchTerm("");
      setPhoneNumber("91");
      setSelectedLead(null);
      setTemplates([]);
      setSelectedTemplate(null);
      setSendingTemplate(false);
      setTemplateSearchTerm("");
    }
  }, [visible]);

  // Fetch templates when moving to template step
  useEffect(() => {
    if (step === 'template' && templates.length === 0) {
      fetchTemplatesList();
    }
  }, [step]);

  const fetchTemplatesList = async () => {
    setLoadingTemplates(true);
    try {
      Logger.log("Fetching WhatsApp templates...");
      const response = await getTemplates({
        page: 1,
        perPage: 100,
        isAscending: false
      });

      if (response.data?.data) {
        setTemplates(response.data.data);
        Logger.log("Templates loaded:", response.data.data.length);
      }
    } catch (err) {
      console.error("Failed to fetch templates:", err);
      toast.error("Failed to load templates");
    } finally {
      setLoadingTemplates(false);
    }
  };

  const debouncedSearch = debounce(async (value: string) => {
    if (!value?.trim()) {
      setSearchResults([]);
      return;
    }

    setLoading(true);
    try {
      Logger.log("Searching for:", value);
      const response = await axios.get(`${API_URL}api/v1/lead/search`, {
        headers: {
          ...getHeaderOptions,
          Authorization: localStorage.getItem("auth_token") || "",
        },
        params: {
          search: value,
          orderBy: "_id",
          isAscending: false,
          page: 1,
          perPage: 50,
        },
      });

      if (response.data?.data) {
        setSearchResults(response.data.data);
      }
    } catch (err) {
      console.error("Search failed:", err);
      toast.error("Search failed");
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  }, 500);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    debouncedSearch(value);
  };

  // When user selects a lead, move to template selection
  const handleLeadSelect = (phone: string, name?: string) => {
    Logger.log("Lead selected:", { phone, name });
    setSelectedLead({ phone, name });
    setStep('template');
  };

  // Go back to search step
  const handleBack = () => {
    setStep('search');
    setSelectedTemplate(null);
    setTemplateSearchTerm("");
  };

  // Send the selected template
  const handleSendTemplate = async () => {
    if (!selectedLead || !selectedTemplate) {
      toast.error("Please select a lead and template");
      return;
    }

    if (selectedTemplate.status !== 'APPROVED') {
      toast.error(`Cannot send template: Status is ${selectedTemplate.status}`);
      return;
    }

    setSendingTemplate(true);
    try {
      Logger.log("Sending template to lead:", {
        phone: selectedLead.phone,
        templateId: selectedTemplate._id,
        templateName: selectedTemplate.name
      });

      await dispatch(sendTemplate({
        phone: selectedLead.phone,
        templateId: selectedTemplate._id,
      })).unwrap();

      toast.success(`Template sent to ${selectedLead.name || selectedLead.phone}`);

      // Refresh conversations to show the new chat
      dispatch(fetchConversations({ page: 1, refresh: true }));

      onClose();
    } catch (err: any) {
      console.error("Failed to send template:", err);
      toast.error(err?.message || "Failed to send template");
    } finally {
      setSendingTemplate(false);
    }
  };

  const isValidPhone = () => {
    const cleanPhone = phoneNumber.replace(/[^0-9]/g, "");
    return cleanPhone.length >= 10 && cleanPhone.length <= 15;
  };

  // Filter templates by search and only show approved ones first
  const filteredTemplates = useMemo(() => {
    let filtered = templates;

    // Filter by search term
    if (templateSearchTerm.trim()) {
      const search = templateSearchTerm.toLowerCase();
      filtered = filtered.filter(t =>
        t.name?.toLowerCase().includes(search) ||
        t.category?.toLowerCase().includes(search)
      );
    }

    // Sort: APPROVED first, then by name
    return filtered.sort((a, b) => {
      if (a.status === 'APPROVED' && b.status !== 'APPROVED') return -1;
      if (a.status !== 'APPROVED' && b.status === 'APPROVED') return 1;
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [templates, templateSearchTerm]);

  // Get template body text for preview
  const getTemplateBody = (template: Template): string => {
    const bodyComponent = template.components?.find(c => c.type === 'BODY');
    return bodyComponent?.text || 'No preview available';
  };

  const renderLeadItem = (item: any) => (
    <List.Item
      key={item._id}
      className="cursor-pointer hover:bg-gray-50 px-4 py-2"
      onClick={() => handleLeadSelect(item.phone, item.name)}
      style={{ cursor: 'pointer' }}
    >
      <div className="flex justify-between w-full">
        <div>
          <div className="font-medium text-base">{item.name || "Unknown"}</div>
          <div className="text-sm text-gray-600">{item.phone}</div>
        </div>
        <div style={{ color: '#1890ff', fontSize: '12px' }}>
          Select →
        </div>
      </div>
    </List.Item>
  );

  const renderTemplateItem = (template: Template) => {
    const isApproved = template.status === 'APPROVED';
    const isSelected = selectedTemplate?._id === template._id;

    return (
      <div
        key={template._id}
        onClick={() => isApproved && setSelectedTemplate(template)}
        style={{
          padding: '12px 16px',
          marginBottom: '8px',
          borderRadius: '8px',
          border: isSelected ? '2px solid #1890ff' : '1px solid #e8e8e8',
          backgroundColor: isSelected ? '#e6f7ff' : (isApproved ? '#fff' : '#f5f5f5'),
          cursor: isApproved ? 'pointer' : 'not-allowed',
          opacity: isApproved ? 1 : 0.6,
          transition: 'all 0.2s ease'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontWeight: 600, fontSize: '14px' }}>{template.name}</span>
            <Badge
              status={isApproved ? 'success' : 'warning'}
              text={template.status}
              style={{ fontSize: '11px' }}
            />
          </div>
          {isSelected && (
            <Check size={18} color="#1890ff" />
          )}
        </div>

        <div style={{
          fontSize: '13px',
          color: '#666',
          lineHeight: '1.4',
          maxHeight: '40px',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}>
          {getTemplateBody(template)}
        </div>

        {template.category && (
          <div style={{
            marginTop: '8px',
            fontSize: '11px',
            color: '#999'
          }}>
            {template.category} • {template.language || 'en'}
          </div>
        )}
      </div>
    );
  };

  // Render search step
  const renderSearchStep = () => (
    <>
      <div className="mb-4">
        <Input.Search
          placeholder="Search by name or number..."
          value={searchTerm}
          onChange={(e) => handleSearch(e.target.value)}
          loading={loading}
          className="fm-gilroy"
          enterButton
        />
      </div>

      <div className="search-results">
        <Spin spinning={loading}>
          {searchResults.length > 0 ? (
            <List>
              <VirtualList
                data={searchResults}
                height={CONTAINER_HEIGHT}
                itemHeight={ITEM_HEIGHT}
                itemKey="id"
              >
                {(item) => renderLeadItem(item)}
              </VirtualList>
            </List>
          ) : searchTerm ? (
            <div className="text-center p-4">
              <p className="fm-gilroy text-gray-500 mb-4">
                No leads found. Enter a phone number to start new chat.
              </p>
              <div className="mt-4">
                <PhoneInput
                  country={"in"}
                  value={phoneNumber}
                  onChange={(value) => setPhoneNumber(value)}
                  containerClass="mb-4"
                  inputClass="w-full"
                  specialLabel=""
                />
                <button
                  className={`w-full p-2 rounded fm-gilroy ${
                    isValidPhone()
                      ? "bg-blue-600 text-white cursor-pointer"
                      : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  }`}
                  onClick={() => handleLeadSelect(phoneNumber)}
                  disabled={!isValidPhone()}
                >
                  Continue with this number →
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center p-4">
              <p className="fm-gilroy text-gray-500">
                Search leads to start a conversation
              </p>
            </div>
          )}
        </Spin>
      </div>
    </>
  );

  // Render template selection step
  const renderTemplateStep = () => (
    <>
      {/* Selected lead info */}
      <div style={{
        padding: '12px 16px',
        backgroundColor: '#f6ffed',
        borderRadius: '8px',
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div>
          <div style={{ fontSize: '12px', color: '#52c41a', marginBottom: '2px' }}>
            Sending to:
          </div>
          <div style={{ fontWeight: 600 }}>
            {selectedLead?.name || selectedLead?.phone}
          </div>
          {selectedLead?.name && (
            <div style={{ fontSize: '12px', color: '#666' }}>
              {selectedLead.phone}
            </div>
          )}
        </div>
        <Button
          size="small"
          onClick={handleBack}
          icon={<ArrowLeft size={14} />}
        >
          Change
        </Button>
      </div>

      {/* Info about templates */}
      <div style={{
        padding: '10px 12px',
        backgroundColor: '#fff7e6',
        borderRadius: '6px',
        marginBottom: '16px',
        fontSize: '12px',
        color: '#d46b08',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '8px'
      }}>
        <MessageSquare size={16} style={{ marginTop: '2px', flexShrink: 0 }} />
        <span>
          WhatsApp requires sending an <strong>approved template</strong> to start a conversation with new contacts.
          Select a template below to begin.
        </span>
      </div>

      {/* Template search */}
      <div className="mb-3">
        <Input
          placeholder="Search templates..."
          value={templateSearchTerm}
          onChange={(e) => setTemplateSearchTerm(e.target.value)}
          allowClear
        />
      </div>

      {/* Template list */}
      <div style={{ maxHeight: '280px', overflowY: 'auto' }}>
        <Spin spinning={loadingTemplates}>
          {filteredTemplates.length > 0 ? (
            filteredTemplates.map(template => renderTemplateItem(template))
          ) : loadingTemplates ? null : (
            <Empty
              description="No templates found"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          )}
        </Spin>
      </div>

      {/* Send button */}
      <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #f0f0f0' }}>
        <Button
          type="primary"
          block
          size="large"
          onClick={handleSendTemplate}
          loading={sendingTemplate}
          disabled={!selectedTemplate || selectedTemplate.status !== 'APPROVED'}
          style={{ height: '44px' }}
        >
          {sendingTemplate
            ? 'Sending Template...'
            : selectedTemplate
              ? `Send "${selectedTemplate.name}"`
              : 'Select a Template'
          }
        </Button>
      </div>
    </>
  );

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {step === 'template' && (
            <Button
              type="text"
              size="small"
              onClick={handleBack}
              icon={<ArrowLeft size={16} />}
              style={{ marginLeft: '-8px' }}
            />
          )}
          <span className="fm-gilroy">
            {step === 'search' ? 'New Chat' : 'Select Template'}
          </span>
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={500}
      bodyStyle={{ maxHeight: "80vh", overflow: "hidden" }}
    >
      {step === 'search' ? renderSearchStep() : renderTemplateStep()}
    </Modal>
  );
};

export default AddChat;
